import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiUsers } from "react-icons/fi"

import { OrganisationsService } from "@/client"
import type { OrganisationCreate, OrganisationUpdate } from "@/client"
import {
  GradientBorder,
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/organisation-form")({
  component: OrganisationForm,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      organisationId: search.organisationId as string | undefined,
    }
  },
})

interface FormData {
  name: string
  description?: string
}

function OrganisationForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const { organisationId } = useSearch({ from: "/_layout/organisation-form" })
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common"])
  const isEditMode = !!organisationId

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>()

  // Fetch organization data if in edit mode
  const { data: organisation, isLoading: orgLoading } = useQuery({
    queryKey: ["organisation", organisationId],
    queryFn: () =>
      OrganisationsService.readOrganisation({
        organisationId: organisationId!,
      }),
    enabled: isEditMode,
  })

  // Reset form when organization data is loaded
  useEffect(() => {
    if (organisation) {
      reset({
        name: organisation.name,
        description: organisation.description || "",
      })
    }
  }, [organisation, reset])

  const createOrgMutation = useMutation({
    mutationFn: async (data: OrganisationCreate) => {
      const result = await OrganisationsService.createOrganisation({
        requestBody: data,
      })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      showToast.showSuccessToast(t("admin:orgForm.created"))
      navigate({ to: "/admin" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to create organization"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const updateOrgMutation = useMutation({
    mutationFn: async (data: OrganisationUpdate) => {
      const result = await OrganisationsService.updateOrganisation({
        organisationId: organisationId!,
        requestBody: data,
      })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      queryClient.invalidateQueries({
        queryKey: ["organisation", organisationId],
      })
      showToast.showSuccessToast(t("admin:orgForm.updated"))
      navigate({ to: "/admin" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to update organization"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleFormSubmit = (data: FormData) => {
    if (isEditMode) {
      updateOrgMutation.mutate({
        name: data.name,
        description: data.description || null,
      })
    } else {
      createOrgMutation.mutate(data as OrganisationCreate)
    }
  }

  if (isEditMode && orgLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  return (
    <Box>
      <Flex
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 2, md: 4 }}
        mb={{ base: 5, md: 8 }}
      >
        <Box flex={1} minW={0} w="100%">
          <Heading size={{ base: "lg", md: "xl" }} color={sc.heading}>
            {isEditMode
              ? t("admin:orgForm.editTitle")
              : t("admin:orgForm.addTitle")}
          </Heading>
          <Text color={sc.textMuted} mt={1} fontSize={{ base: "sm", md: "md" }}>
            {isEditMode
              ? t("admin:orgForm.editSubtitle")
              : t("admin:orgForm.addSubtitle")}
          </Text>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/admin" })}
          size="sm"
          alignSelf={{ base: "flex-end", md: "auto" }}
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Flex justify="center">
        <Box w="100%" maxW={{ base: "100%", md: "600px" }}>
          <GradientBorder borderRadius="20px" p={0}>
            <VisionCard
              variant="elevated"
              p={{ base: 4, md: 8 }}
              borderRadius="20px"
            >
              <Flex align="center" gap={3} mb={6}>
                <IconBox variant="glass" size="md">
                  <FiUsers fontSize="20px" color={sc.iconColor} />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("admin:orgForm.configTitle")}
                </Heading>
              </Flex>

              <VStack
                gap={6}
                as="form"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <VStack w="100%" gap={4}>
                  <Field
                    required
                    invalid={!!errors.name}
                    errorText={errors.name?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("admin:orgForm.name")}
                    </Text>
                    <VisionInput
                      {...register("name", {
                        required: t("admin:orgForm.nameRequired"),
                        minLength: {
                          value: 2,
                          message: t("common:validation.nameMinLength"),
                        },
                      })}
                      placeholder={t("admin:orgForm.namePlaceholder")}
                      size="lg"
                      variant="glass"
                    />
                  </Field>

                  <Field
                    invalid={!!errors.description}
                    errorText={errors.description?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("admin:orgForm.description")}
                    </Text>
                    <VisionInput
                      {...register("description")}
                      placeholder={t("admin:orgForm.descriptionPlaceholder")}
                      size="lg"
                      variant="glass"
                    />
                  </Field>
                </VStack>

                <HStack gap={3} w="100%">
                  <VisionButton
                    type="button"
                    variant="ghost"
                    size="lg"
                    onClick={() => navigate({ to: "/admin" })}
                  >
                    {t("common:actions.cancel")}
                  </VisionButton>
                  <VisionButton
                    type="submit"
                    variant="brand"
                    size="lg"
                    flex="1"
                    loading={
                      createOrgMutation.isPending || updateOrgMutation.isPending
                    }
                  >
                    {createOrgMutation.isPending || updateOrgMutation.isPending
                      ? isEditMode
                        ? t("common:status.updating")
                        : t("common:status.creating")
                      : isEditMode
                        ? t("common:actions.update")
                        : t("common:actions.create")}
                  </VisionButton>
                </HStack>
              </VStack>
            </VisionCard>
          </GradientBorder>
        </Box>
      </Flex>
    </Box>
  )
}
